import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import type { WorkflowRunNodeStatus } from '@/apis/logiblocksAPI';
import { formatUserDateTime } from '@/common/utils/userDateFormat';
import type { DateTimeValue } from '@/proto/logiblocks/logiblocks';
import { DelaySkippedReason } from '@/proto/logiblocks/node_run_report';
import type { RunStep } from '../../runHistory/runStepUtils';

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Returns `time` in its own zone, in the `dateFormat` preference, followed by the zone's name,
 * such as "Nov 03, 2026 2:00 PM (America/New_York)".
 */
function formatTargetTime(
  time: DateTimeValue,
  dateFormat: string | undefined
): string {
  const formatted = formatUserDateTime(
    dayjs(time.unix_millis).tz(time.iana_timezone),
    dateFormat
  );
  return `${formatted} (${time.iana_timezone})`;
}

/** Returns what a Delay with `status` did about its wait until `targetTime`. */
function getWaitSummary(
  status: WorkflowRunNodeStatus,
  targetTime: string
): string {
  switch (status) {
    case 'RUNNING':
      return `Waiting until ${targetTime}`;
    case 'FAILED':
      return `Was waiting until ${targetTime}`;
    case 'COMPLETED':
    case 'SKIPPED_CHILDREN':
      return `Waited until ${targetTime}`;
    default:
      console.error(`Unexpected run step status: ${String(status)}`);
      return `Waited until ${targetTime}`;
  }
}

/**
 * Returns what a Delay's step did: the time it waits or waited until, or why it didn't wait. Null
 * if the step has no Delay report, or the report is malformed.
 */
export function getDelayRunStepSummary(
  step: RunStep,
  dateFormat: string | undefined
): string | null {
  const report = step.report?.delay_action;
  if (report === undefined) {
    return null;
  }
  const reason = report.delay_skipped_reason;
  switch (reason) {
    case DelaySkippedReason.DELAY_SKIPPED_REASON_NOT_SKIPPED:
    case DelaySkippedReason.DELAY_SKIPPED_REASON_SKIP_DELAYS: {
      if (report.target_time === undefined) {
        console.error(
          `Delay report has no target time for skipped reason ${reason}`
        );
        return null;
      }
      const targetTime = formatTargetTime(report.target_time, dateFormat);
      if (reason === DelaySkippedReason.DELAY_SKIPPED_REASON_SKIP_DELAYS) {
        return `Skipped ahead to ${targetTime}`;
      }
      return getWaitSummary(step.status, targetTime);
    }
    case DelaySkippedReason.DELAY_SKIPPED_REASON_LOCATION_OPEN:
      return 'Location was open';
    case DelaySkippedReason.DELAY_SKIPPED_REASON_NO_HOURS_SET:
      return 'Location has no opening hours';
    case DelaySkippedReason.DELAY_SKIPPED_REASON_LOCATION_NOT_FOUND:
      return 'Location not found';
    case DelaySkippedReason.DELAY_SKIPPED_REASON_NO_OPENING_AHEAD:
      return 'No upcoming opening';
    case DelaySkippedReason.DELAY_SKIPPED_REASON_UNSPECIFIED:
    case DelaySkippedReason.UNRECOGNIZED:
    default:
      console.error(`Delay report has no valid skipped reason: ${reason}`);
      return null;
  }
}
